import React, { useCallback, useRef, useState } from 'react'
import FWOTextInput from '../form/FWOTextInput'
import { Form, Formik, FormikHelpers } from 'formik'
import useErrorHandler from '../../hooks/useErrorHandler'
import {
  IUnpaidWagesProps,
  UnpaidWageSearchFieldMapping,
  UnpaidWageSearchFormValues,
  UnpaidWageSearchSubmitRequest,
} from '../../models/UnpaidWages'
import { Button } from 'semantic-ui-react'
import { observer } from 'mobx-react-lite'
import * as Yup from 'yup'
import UnpaidWagesHeader from './UnpaidWagesHeader'
import FormErrorDisplay from '../common/FormErrorMessages'
import UnpaidWageService from '../../services/UnpaidWageService'
import Conditional from '../common/Conditional'
import UnpaidWagesNoMatch from './UnpaidWagesNoMatch'
import FWOGoogleCaptcha from '../form/FWOGoogleCaptcha'
import UnpaidWagesFooter from './UnpaidWagesFooter'
import { FormRegex } from '../../models/Common'
import { ValidationMessage } from '../../models/ValidationMessage'

const UnpaidWagesSearch = observer((props: IUnpaidWagesProps) => {
  const formRef = useRef(null)
  const formErrorRef = useRef(null)
  const captchaRef = useRef(null)
  const [userErrors, addErrors, handleErrors, , clearErrors] = useErrorHandler()
  const [unpaidWageSearchRequest] = useState<UnpaidWageSearchFormValues>(
    new UnpaidWageSearchFormValues()
  )
  const [match, setMatch] = useState({ isMatch: true })
  const [hasError, setHasError] = useState({ hasError: false })
  const [ariaLabels, setAriaLabels] = useState({
    familyName: UnpaidWageSearchFieldMapping.get('familyName'),
    abn: UnpaidWageSearchFieldMapping.get('abn'),
    entityName: UnpaidWageSearchFieldMapping.get('entityName'),
  })
  const validateSchema = Yup.object().shape(
    {
      familyName: Yup.string()
        .required(ValidationMessage.Required)
        .typeError(ValidationMessage.Required),
      recaptcha: Yup.string()
        .required(ValidationMessage.Required)
        .typeError(ValidationMessage.Required),
      abn: Yup.string().when('entityName', {
        is: (entityName) => !entityName || entityName.length === 0,
        then: Yup.string()
          .matches(FormRegex.PositiveNumber, 'ABN is required')
          .required(ValidationMessage.ABNRequiredIfEntityEmpty)
          .typeError(ValidationMessage.ABNRequiredIfEntityEmpty),
        otherwise: Yup.string(),
      }),
      entityName: Yup.string().when('abn', {
        is: (abn) => !abn || abn.length === 0,
        then: Yup.string()
          .required(ValidationMessage.EntityRequiredIfABNEmpty)
          .typeError(ValidationMessage.EntityRequiredIfABNEmpty),
        otherwise: Yup.string(),
      }),
    },
    [['abn', 'entityName']]
  )

  const friendlyErrors = (errors: any, fieldMapping: Map<string, string>) => {
    let msg = ''
    const errorList: string[] = []
    const keys = Object.keys(errors)
    keys?.map((key) => {
      const error = fieldMapping.get(key)
      if (key.toLowerCase() === 'abn') {
        msg = `${error}: ${ValidationMessage.ABNRequiredIfEntityEmpty}`
        errorList.push(msg)
        setAriaLabels((prevState) => ({
          ...prevState,
          abn: msg,
        }))
      } else if (key.toLowerCase() === 'entityname') {
        msg = `${error}: ${ValidationMessage.EntityRequiredIfABNEmpty}`
        errorList.push(msg)
        setAriaLabels((prevState) => ({
          ...prevState,
          entityName: msg,
        }))
      } else if (key.toLowerCase() === 'familyname') {
        msg = `${error}: ${ValidationMessage.Required}`
        errorList.push(msg)
        setAriaLabels((prevState) => ({
          ...prevState,
          familyName: msg,
        }))
      } else {
        errorList.push(`${error}: ${ValidationMessage.Required}`)
      }
    })
    return errorList
  }

  const submit = async () => {
    clearErrors()
    const formErrors = await formRef.current.validateForm()
    const errorKeys = Object.keys(formErrors)
    if (errorKeys.length > 0) {
      addErrors(friendlyErrors(formErrors, UnpaidWageSearchFieldMapping))
      window.scrollTo(0, 0)
      setHasError((prevState) => ({
        ...prevState,
        hasError: true,
      }))
      formErrorRef.current?.focus()
    }
    formRef.current.submitForm()
  }

  const submitFeedbackForm = async (values: UnpaidWageSearchFormValues) => {
    const payload = new UnpaidWageSearchSubmitRequest(values)
    return await UnpaidWageService.submit(payload)
  }

  const handleSubmit = useCallback(
    (
      formFields: UnpaidWageSearchFormValues,
      actions: FormikHelpers<UnpaidWageSearchFormValues>
    ) => {
      return submitFeedbackForm(formFields)
        .then((response) => {
          if (response === null || response.result.id === null) {
            setMatch((prevState) => ({
              ...prevState,
              isMatch: false,
            }))
            return
          }
          props.onSave(response.result.id)
        })
        .catch((error) => {
          if (captchaRef.current) {
            captchaRef.current.refresh()
          }
          handleErrors(error)
        })
        .finally(() => {
          actions.setSubmitting(false)
        })
    },
    []
  )

  const startAgain = () => {
    setMatch((prevState) => ({
      ...prevState,
      isMatch: true,
    }))
  }

  return (
    <>
      <FormErrorDisplay
        show={userErrors?.length > 0 || hasError.hasError}
        errorsList={userErrors}
        heading="Please review the following errors"
        errorRef={formErrorRef}
      />
      <Conditional if={!match.isMatch}>
        <UnpaidWagesNoMatch reset={startAgain} />
      </Conditional>
      <Conditional if={match.isMatch}>
        <UnpaidWagesHeader />
        <div>
          <fieldset className="wages-fieldset">
            <h3> Step 1</h3>
            <p>
              Enter the employee&apos;s family name (last name), for example,
              Smith.
            </p>
            <Formik
              innerRef={formRef}
              initialValues={unpaidWageSearchRequest}
              validationSchema={validateSchema}
              validateOnMount={false}
              onSubmit={async (values, actions) => {
                await handleSubmit(values, actions)
              }}
            >
              {({ isSubmitting }) => (
                <Form className="ui form osp-variant large">
                  <FWOTextInput
                    type="text"
                    placeholder=""
                    name="familyName"
                    label={UnpaidWageSearchFieldMapping.get('familyName')}
                    ariaLabel={ariaLabels.familyName}
                    required={true}
                    showreadonly={false}
                    limit={255}
                  />
                  <hr />
                  <h3>Step 2</h3>
                  <p>
                    Enter at least <b>one</b> of the following: the Australian
                    Business Number (ABN) <b>or</b> the business name (also
                    called entity name) of the employer.
                  </p>

                  <FWOTextInput
                    type="text"
                    placeholder=""
                    name="abn"
                    label={UnpaidWageSearchFieldMapping.get('abn')}
                    ariaLabel={ariaLabels.abn}
                    required={true}
                    showreadonly={false}
                    limit={20}
                  />
                  <FWOTextInput
                    type="text"
                    placeholder=""
                    name="entityName"
                    label={UnpaidWageSearchFieldMapping.get('entityName')}
                    ariaLabel={ariaLabels.entityName}
                    required={true}
                    showreadonly={false}
                    limit={255}
                  />
                  <FWOGoogleCaptcha ref={captchaRef} name="recaptcha" />
                  <Button
                    onClick={() => submit()}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    type="button"
                    className="is-primary"
                  >
                    Submit
                  </Button>
                </Form>
              )}
            </Formik>
          </fieldset>
          <UnpaidWagesFooter />
        </div>
      </Conditional>
    </>
  )
})

export default UnpaidWagesSearch
